import React, { useState } from "react";
import { MapPin, Clock, UserCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, Input, Select, MultiSelect, TagGroup, Toggle, Textarea, TimePicker } from "./FormComponents";

export const Step4 = ({ data, updateData, buildingAccess, isLight, showValidation }) => {
  const [methodError, setMethodError] = useState("");
  const [idError, setIdError] = useState("");

  const handleChange = (field, value) => {
    updateData("checkIn", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("checkIn", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleMethodChange = (val) => {
    setMethodError("");
    
    const updates = { methods: val };
    
    // Clear data for methods no longer selected
    if (!val.includes("Self Check-in")) {
      updates.selfCheckIn = { 
        mainEntranceAccessType: "No, guests can enter the building freely",
        mainEntranceAccessFrom: "",
        mainEntranceAccessUntil: "",
        deviceType: "",
        location: "",
        accessCodeTiming: "",
        accessCodeChannels: [],
        accessCodeChannelOther: "",
        instructions: "",
        lockboxContent: "",
        hasDifferentDevices: false
      };
      updates.guestId = {
        ...data.guestId,
        requiredForAccessCode: false
      };
    }
    
    if (!val.includes("Meet on Arrival")) {
      updates.meetOnArrival = {
        from: "",
        until: "",
        meetingPoint: "",
        contactPerson: "",
        instructions: "",
        afterWindowInstructions: "",
        isRequiredDuringHours: false
      };
    }
    
    if (!val.includes("Reception/Office")) {
      updates.reception = {
        location: "",
        openFrom: "",
        openUntil: "",
        checkInRequiredDuringOfficeHours: false
      };
    }

    const hasInPersonMethod = val.includes("Reception/Office") || val.includes("Meet on Arrival");
    if (data.guestId.channels.includes("Present in Person") && !hasInPersonMethod) {
      updates.guestId = {
        ...data.guestId,
        channels: data.guestId.channels.filter(c => c !== "Present in Person")
      };
      setIdError("Removed 'Present in Person' because no in-person check-in method is selected.");
    } else {
      setIdError("");
    }

    updateData("checkIn", { ...data, ...updates });
  };

  const handleChannelChange = (val) => {
    const isPresentInPerson = val.includes("Present in Person");
    const hasInPersonMethod = data.methods.includes("Reception/Office") || data.methods.includes("Meet on Arrival");
    if (isPresentInPerson && !hasInPersonMethod) {
      setIdError("To select 'Present in Person', you must first select 'Reception/Office' or 'Meet on Arrival' as a check-in method.");
      return;
    }
    setIdError("");
    handleNestedChange("guestId", "channels", val);
  };

  const renderSelfCheckInDetails = (details, parent, title, isNested = false) => {
    if (!details) return null;
    const bgColor = isLight
      ? isNested ? "bg-neutral-50/50" : "bg-neutral-100/50"
      : isNested ? "bg-white/5" : "bg-white/10";
    const border = isLight
      ? isNested ? "border-neutral-200/50" : "border-neutral-200"
      : isNested ? "border-white/5" : "border-white/10";

    const handleSubNestedChange = (field, value) => {
      if (parent === "selfCheckIn") {
        handleNestedChange("selfCheckIn", field, value);
      } else {
        handleNestedChange("selfCheckIn", parent, { ...details, [field]: value });
      }
    };

    return (
      <div className={`${bgColor} p-6 rounded-xl border ${border} flex flex-col gap-6`}>
        <h4 className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Access device"
            options={["Lockbox", "Smart Lock"]}
            value={details.deviceType || ""}
            isLight={isLight}
            onChange={(e) => {
              const val = e.target.value;
              const updates = { deviceType: val };

              if (parent === "selfCheckIn") {
                updateData("checkIn", { ...data, selfCheckIn: { ...data.selfCheckIn, ...updates } });
              } else {
                updateData("checkIn", { ...data, selfCheckIn: { ...data.selfCheckIn, [parent]: { ...details, ...updates } } });
              }
            }}
            required
          />
          <Input
            label="Device location"
            placeholder="Next to the apartment door on the left"
            value={details.location || ""}
            isLight={isLight}
            onChange={(e) => handleSubNestedChange("location", e.target.value)}
            required
          />
          {details.deviceType === "Lockbox" && (
            <>
              {parent === "accommodation" ? (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                  <Select
                    label="What is inside the lockbox?"
                    options={["Physical key", "Key card", "Other access item"]}
                    value={details.lockboxContent || ""}
                    isLight={isLight}
                    onChange={(e) => handleSubNestedChange("lockboxContent", e.target.value)}
                    required
                  />
                  {data.selfCheckIn?.buildingEntrance?.deviceType === "Lockbox" && (
                    <Select
                      label="Does this lockbox also contain the access item for the main building?"
                      options={["Yes", "No"]}
                      value={details.containsBuildingAccessItem ? "Yes" : "No"}
                      isLight={isLight}
                      onChange={(e) => handleSubNestedChange("containsBuildingAccessItem", e.target.value === "Yes")}
                      required
                    />
                  )}
                  <Input
                    label="How can guests identify the lockbox?"
                    placeholder="101 or A or Blue"
                    value={details.identification || ""}
                    isLight={isLight}
                    onChange={(e) => handleSubNestedChange("identification", e.target.value)}
                  />
                  <Select
                    label="When will guests receive the access code?"
                    options={["upon booking", "48h before arrival", "24h before arrival", "Upon arrival"]}
                    value={details.accessCodeTiming || ""}
                    isLight={isLight}
                    onChange={(e) => handleSubNestedChange("accessCodeTiming", e.target.value)}
                    required
                  />
                </div>
              ) : (
                <>
                  <Select
                    label="What is inside the lockbox?"
                    options={["Physical key", "Key card", "Other access item"]}
                    value={details.lockboxContent || ""}
                    isLight={isLight}
                    onChange={(e) => handleSubNestedChange("lockboxContent", e.target.value)}
                    required
                  />
                  <Input
                    label="How can guests identify the lockbox?"
                    placeholder="101 or A or Blue"
                    value={details.identification || ""}
                    isLight={isLight}
                    onChange={(e) => handleSubNestedChange("identification", e.target.value)}
                  />
                </>
              )}
              {details.lockboxContent === "Other access item" && (
                <Input
                  label="Specify other access item"
                  placeholder="Fob, Remote"
                  value={details.otherLockboxContent || ""}
                  isLight={isLight}
                  onChange={(e) => handleSubNestedChange("otherLockboxContent", e.target.value)}
                  required
                />
              )}
            </>
          )}
          {!(parent === "accommodation" && details.deviceType === "Lockbox") && (
            <Select
              label="When will guests receive the access code?"
              options={["upon booking", "48h before arrival", "24h before arrival", "Upon arrival"]}
              value={details.accessCodeTiming || ""}
              isLight={isLight}
              onChange={(e) => handleSubNestedChange("accessCodeTiming", e.target.value)}
              required
            />
          )}
          <div className="flex flex-col gap-4">
            <MultiSelect
              label="Where will guests receive the access code?"
              options={["Email", "WhatsApp", "Viber", "SMS", "Booking platform chat", "Other"]}
              value={details.accessCodeChannels || []}
              isLight={isLight}
              required
              hideSearch={true}
              onChange={(val) => handleSubNestedChange("accessCodeChannels", val)}
            />
            {(details.accessCodeChannels || []).includes("Other") && (
              <Input
                label="Specify other channel"
                placeholder="e.g. Telegram, Property app..."
                value={details.accessCodeChannelOther || ""}
                isLight={isLight}
                onChange={(e) => handleSubNestedChange("accessCodeChannelOther", e.target.value)}
                required
              />
            )}
          </div>
          <div className="md:col-span-2">
            <Textarea
              label="Access device instructions"
              placeholder="Enter code → press switch down → pull door open..."
              value={details.instructions || ""}
              isLight={isLight}
              onChange={(e) => handleSubNestedChange("instructions", e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSingleSelfCheckInInputs = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Access device"
          options={["Lockbox", "Smart Lock"]}
          value={data.selfCheckIn?.deviceType || ""}
          isLight={isLight}
          onChange={(e) => {
            const val = e.target.value;
            const updates = { deviceType: val };
            updateData("checkIn", { ...data, selfCheckIn: { ...data.selfCheckIn, ...updates } });
          }}
          required
        />
        {data.selfCheckIn?.deviceType === "Lockbox" && (
          <>
            <Select
              label="What is inside the lockbox?"
              options={["Physical key", "Key card", "Other access item"]}
              value={data.selfCheckIn?.lockboxContent || ""}
              isLight={isLight}
              onChange={(e) => handleNestedChange("selfCheckIn", "lockboxContent", e.target.value)}
              required
            />
            {data.selfCheckIn?.lockboxContent === "Other access item" && (
              <Input
                label="Specify other access item"
                placeholder="Fob, Remote"
                value={data.selfCheckIn?.otherLockboxContent || ""}
                isLight={isLight}
                onChange={(e) => handleNestedChange("selfCheckIn", "otherLockboxContent", e.target.value)}
                required
              />
            )}
          </>
        )}
        <Input
          label="Device location"
          placeholder="Next to the apartment door on the left"
          value={data.selfCheckIn?.location || ""}
          isLight={isLight}
          onChange={(e) => handleNestedChange("selfCheckIn", "location", e.target.value)}
          required
        />
        {data.selfCheckIn?.deviceType === "Lockbox" && (
          <Input
            label="How can guests identify the lockbox?"
            placeholder="101 or A or Blue"
            value={data.selfCheckIn?.identification || ""}
            isLight={isLight}
            onChange={(e) => handleNestedChange("selfCheckIn", "identification", e.target.value)}
          />
        )}
        <Select
          label="When will guests receive the access code?"
          options={["upon booking", "48h before arrival", "24h before arrival", "Upon arrival"]}
          value={data.selfCheckIn?.accessCodeTiming || ""}
          isLight={isLight}
          onChange={(e) => handleNestedChange("selfCheckIn", "accessCodeTiming", e.target.value)}
          required
        />
        <div className="flex flex-col gap-4">
          <MultiSelect
            label="Where will guests receive the access code?"
            options={["Email", "WhatsApp", "Viber", "SMS", "Booking platform chat", "Other"]}
            value={data.selfCheckIn?.accessCodeChannels || []}
            isLight={isLight}
            required
            hideSearch={true}
            onChange={(val) => handleNestedChange("selfCheckIn", "accessCodeChannels", val)}
          />
          {(data.selfCheckIn?.accessCodeChannels || []).includes("Other") && (
            <Input
              label="Specify other channel"
              placeholder="e.g. Telegram, Property app..."
              value={data.selfCheckIn?.accessCodeChannelOther || ""}
              isLight={isLight}
              onChange={(e) => handleNestedChange("selfCheckIn", "accessCodeChannelOther", e.target.value)}
              required
            />
          )}
        </div>
        <div className="md:col-span-2">
          <Textarea
            label="Access device instructions"
            placeholder="Enter code → press switch down → pull door open..."
            value={data.selfCheckIn?.instructions || ""}
            isLight={isLight}
            onChange={(e) => handleNestedChange("selfCheckIn", "instructions", e.target.value)}
          />
        </div>
      </div>
    );
  };

  const renderMethodDetails = (method) => {
    const bgColor = isLight ? "bg-neutral-100/50" : "bg-white/10";
    const border = isLight ? "border-neutral-200" : "border-white/10";

    switch (method) {
      case "Reception/Office":
        return (
          <div className={`${bgColor} p-6 rounded-xl border ${border} flex flex-col gap-6`}>
            <h4 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Reception Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Where is the reception office?"
                placeholder="1st floor, room 102"
                value={data.reception?.location || ""}
                isLight={isLight}
                onChange={(e) => handleNestedChange("reception", "location", e.target.value)}
                required
              />
              <div className="flex gap-2">
                <TimePicker
                  label="Open from"
                  value={data.reception?.openFrom || "08:00"}
                  isLight={isLight}
                  onChange={(val) => handleNestedChange("reception", "openFrom", val)}
                  required
                />
                <TimePicker
                  label="Until"
                  value={data.reception?.openUntil || "20:00"}
                  isLight={isLight}
                  onChange={(val) => handleNestedChange("reception", "openUntil", val)}
                  required
                />
              </div>
            </div>
            {data.methods.includes("Reception/Office") && data.methods.includes("Self Check-in") && (
              <div className="pt-4 border-t border-white/5">
                <Toggle
                  label="Is reception check-in required during office hours?"
                  value={data.reception?.checkInRequiredDuringOfficeHours || false}
                  isLight={isLight}
                  onChange={(val) => handleNestedChange("reception", "checkInRequiredDuringOfficeHours", val)}
                />
              </div>
            )}
          </div>
        );
      case "Meet on Arrival":
        return (
          <div className={`${bgColor} p-6 rounded-xl border ${border} flex flex-col gap-6`}>
            <h4 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Meet on Arrival Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-2">
                <TimePicker
                  label="From"
                  value={data.meetOnArrival?.from || "08:00"}
                  isLight={isLight}
                  onChange={(val) => handleNestedChange("meetOnArrival", "from", val)}
                  required
                />
                <TimePicker
                  label="Until"
                  value={data.meetOnArrival?.until || "20:00"}
                  isLight={isLight}
                  onChange={(val) => handleNestedChange("meetOnArrival", "until", val)}
                  required
                />
              </div>
              {data.methods.includes("Self Check-in") && data.methods.includes("Meet on Arrival") && !data.methods.includes("Reception/Office") && (
                <div className="md:col-span-2 pt-2 border-t border-white/5">
                  <Toggle
                    label="Is meet-on-arrival check-in required during these hours?"
                    value={data.meetOnArrival?.isRequiredDuringHours || false}
                    isLight={isLight}
                    onChange={(val) => handleNestedChange("meetOnArrival", "isRequiredDuringHours", val)}
                  />
                </div>
              )}
              <Input
                label="Meeting point"
                placeholder="At the main entrance of the building"
                value={data.meetOnArrival?.meetingPoint || ""}
                isLight={isLight}
                onChange={(e) => handleNestedChange("meetOnArrival", "meetingPoint", e.target.value)}
                required
              />
              <div className="md:col-span-2">
                <Textarea
                  label="Meeting instructions"
                  placeholder="Please call us 30 minutes before arrival..."
                  value={data.meetOnArrival?.instructions || ""}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("meetOnArrival", "instructions", e.target.value)}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Textarea
                  label="What if guests arrive outside this window?"
                  placeholder="After 20:00, keys will be left in the lockbox..."
                  value={data.meetOnArrival?.afterWindowInstructions || ""}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("meetOnArrival", "afterWindowInstructions", e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        );
      case "Self Check-in":
        const showMainEntranceQuestion = buildingAccess?.hasMainEntrance;
        const hasRestrictions = showMainEntranceQuestion && data.selfCheckIn?.mainEntranceAccessType && data.selfCheckIn.mainEntranceAccessType !== "No, guests can enter the building freely";

        return (
          <div className={`${bgColor} p-6 rounded-xl border ${border} flex flex-col gap-6`}>
            <h4 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Self Check-in</h4>

            {showMainEntranceQuestion && (
              <div className="pb-4 border-b border-white/5 flex flex-col gap-4">
                <span className={`text-sm font-medium ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>During check-in, does the main building entrance require a key, code, or other access method for guests to enter?</span>
                <div className="flex flex-col gap-2">
                  {[
                    "No, guests can enter the building freely",
                    "Yes, only during certain hours",
                    "Yes, every time they enter."
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        const updates = { mainEntranceAccessType: option };
                        if (option !== "Yes, only during certain hours") {
                          updates.mainEntranceAccessFrom = "";
                          updates.mainEntranceAccessUntil = "";
                        } else {
                          if (!data.selfCheckIn?.mainEntranceAccessFrom) updates.mainEntranceAccessFrom = "08:00";
                          if (!data.selfCheckIn?.mainEntranceAccessUntil) updates.mainEntranceAccessUntil = "20:00";
                        }
                        updateData("checkIn", {
                          ...data,
                          selfCheckIn: {
                            ...data.selfCheckIn,
                            ...updates
                          }
                        });
                      }}
                      className={`text-left px-5 py-4 rounded-3xl border text-sm transition-all ${data.selfCheckIn?.mainEntranceAccessType === option
                          ? isLight
                            ? "bg-neutral-900 border-neutral-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.55)]"
                            : "bg-white/20 border-white/10 text-white shadow-[0_12px_30px_rgba(0,0,0,0.8)]"
                          : isLight
                            ? "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50 shadow-sm"
                            : "bg-white/5 border-white/5 text-neutral-400 hover:border-white/15 hover:bg-white/10"
                        }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {data.selfCheckIn?.mainEntranceAccessType === "Yes, only during certain hours" && (
                  <div className="flex items-center gap-2 mt-2">
                    <TimePicker
                      label="From"
                      value={data.selfCheckIn?.mainEntranceAccessFrom || "08:00"}
                      isLight={isLight}
                      onChange={(val) => handleNestedChange("selfCheckIn", "mainEntranceAccessFrom", val)}
                      required
                    />
                    <TimePicker
                      label="Until"
                      value={data.selfCheckIn?.mainEntranceAccessUntil || "20:00"}
                      isLight={isLight}
                      onChange={(val) => handleNestedChange("selfCheckIn", "mainEntranceAccessUntil", val)}
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {hasRestrictions ? (
              <div className="flex flex-col gap-6">
                <div className={`pb-4 border-b ${isLight ? 'border-neutral-200' : 'border-white/5'}`}>
                  <Toggle
                    label="Does the main building entrance have a separate access method or device from the accommodation door?"
                    subtitle="Guests may need to unlock, open, or access a main building entrance before reaching the accommodation door. This applies even if both entrances use the same type of method, such as two separate lockboxes or two separate codes."
                    value={data.selfCheckIn?.hasDifferentDevices || false}
                    isLight={isLight}
                    onChange={(val) => {
                      const updates = { hasDifferentDevices: val };
                      if (val) {
                        // Initialize sub-objects if they don't exist so the UI can render them
                        if (!data.selfCheckIn?.buildingEntrance) {
                          updates.buildingEntrance = {
                            deviceType: "",
                            location: "",
                            instructions: "",
                            lockboxContent: "",
                            identification: "",
                            accessCodeTiming: "",
                            accessCodeChannels: [],
                            accessCodeChannelOther: ""
                          };
                        }
                        if (!data.selfCheckIn?.accommodation) {
                          updates.accommodation = {
                            deviceType: "",
                            location: "",
                            instructions: "",
                            lockboxContent: "",
                            identification: "",
                            accessCodeTiming: "",
                            accessCodeChannels: [],
                            accessCodeChannelOther: "",
                            containsBuildingAccessItem: false
                          };
                        }
                      }
                      updateData("checkIn", {
                        ...data,
                        selfCheckIn: {
                          ...data.selfCheckIn,
                          ...updates
                        }
                      });
                    }}
                  />
                </div>
                {data.selfCheckIn?.hasDifferentDevices ? (
                  <div className="flex flex-col gap-8">
                    {renderSelfCheckInDetails(data.selfCheckIn.buildingEntrance, "buildingEntrance", "Self Check-in Details for the main building entrance", true)}
                    {renderSelfCheckInDetails(data.selfCheckIn.accommodation, "accommodation", "Self Check-in Details for the accommodation door", true)}
                  </div>
                ) : renderSingleSelfCheckInInputs()}
              </div>
            ) : renderSingleSelfCheckInInputs()}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Check-in Method" icon={MapPin} subtitle="How guests access the property on arrival.">
        <div className="flex flex-col gap-8">
          <TagGroup
            label="Check-in methods"
            subtitle="Select all check-in methods that apply"
            required options={["Reception/Office", "Self Check-in", "Meet on Arrival"]}
            value={data.methods}
            isLight={isLight}
            showValidation={showValidation}
            onChange={handleMethodChange}
            error={methodError}
          />
          {data.methods.includes("Reception/Office") && renderMethodDetails("Reception/Office")}
          {data.methods.includes("Self Check-in") && renderMethodDetails("Self Check-in")}
          {data.methods.includes("Meet on Arrival") && renderMethodDetails("Meet on Arrival")}
        </div>
      </Card>

      <Card title="Times & Policies" icon={Clock} subtitle="Set your check-in time and early check-in options." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Check-in policy" options={["Specific Time", "Time Window"]}
              value={data.checkInType}
              isLight={isLight}
              onChange={(e) => {
                const val = e.target.value;
                const updates = { checkInType: val };
                if (val === "Specific Time") {
                  updates.checkInUntil = "";
                }
                updateData("checkIn", { ...data, ...updates });
              }}
              required
            />
            {data.checkInType === "Specific Time" && (
              <TimePicker
                label="From specific time onward"
                required
                value={data.checkInFrom || "15:00"}
                isLight={isLight}
                onChange={(val) => handleChange("checkInFrom", val)}
              />
            )}
            {data.checkInType === "Time Window" && (
              <div className="flex gap-2">
                <TimePicker
                  label="From"
                  required
                  value={data.checkInFrom || "15:00"}
                  isLight={isLight}
                  onChange={(val) => handleChange("checkInFrom", val)}
                />
                <TimePicker
                  label="Until"
                  required
                  value={data.checkInUntil || "22:00"}
                  isLight={isLight}
                  onChange={(val) => handleChange("checkInUntil", val)}
                />
              </div>
            )}
          </div>
          <Toggle
            label="Early check-in available upon request?"
            value={data.earlyCheckIn.available}
            isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) {
                updates.feeApplicable = false;
                updates.feeAmount = "";
                updates.feeCurrency = "€";
              }
              updateData("checkIn", {
                ...data,
                earlyCheckIn: { ...data.earlyCheckIn, ...updates }
              });
            }}
          >
            <Toggle
              label="Is there an early check-in fee?"
              value={data.earlyCheckIn.feeApplicable}
              isLight={isLight}
              onChange={(val) => handleNestedChange("earlyCheckIn", "feeApplicable", val)}
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-400">Early check-in fee <span className="text-red-500">*</span></label>
                <div className="flex gap-3 items-start max-w-[240px]">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="20"
                    value={data.earlyCheckIn.feeAmount}
                    isLight={isLight}
                    showValidation={showValidation}
                    className="w-32"
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, "");
                      const parts = val.split(".");
                      const filteredVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val;
                      handleNestedChange("earlyCheckIn", "feeAmount", filteredVal);
                    }}
                    required
                  />
                  <Select
                    options={["€"]} className="w-20"
                    value={data.earlyCheckIn.feeCurrency}
                    isLight={isLight}
                    showValidation={showValidation}
                    hidePlaceholder
                    onChange={(e) => handleNestedChange("earlyCheckIn", "feeCurrency", e.target.value)}
                  />
                </div>
              </div>
            </Toggle>
          </Toggle>
        </div>
      </Card>

      <Card title="Guest Identification" icon={UserCheck} subtitle="How guests verify their identity before arriving.">
        <div className="flex flex-col gap-8">
          <Toggle
            label="Do guests need to provide identification documents?"
            value={data.guestId.required}
            isLight={isLight}
            onChange={(val) => handleNestedChange("guestId", "required", val)}
          />
          <AnimatePresence>
            {data.guestId.required && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="flex flex-col gap-8 overflow-hidden">
                {data.methods.includes("Self Check-in") && (
                  <Toggle
                    label="Do guests need to send identification documents in order to receive the self check-in access code?"
                    value={data.guestId.requiredForAccessCode || false}
                    isLight={isLight}
                    onChange={(val) => handleNestedChange("guestId", "requiredForAccessCode", val)}
                  />
                )}
                <div className="pt-6 border-t border-white/5 flex flex-col gap-8">
                  <TagGroup
                    label="How should guests send their documents?" required={data.guestId.required}
                    options={["Email Address", "Messaging Apps", "Booking Platform Chat", "Present in Person"]}
                    value={data.guestId.channels}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={handleChannelChange}
                    error={idError}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {data.guestId.channels.includes("Email Address") && (
                      <Input
                        label="Email address" type="email" placeholder="sendyourid@gmail.com"
                        value={data.guestId.email}
                        isLight={isLight}
                        showValidation={showValidation}
                        onChange={(e) => handleNestedChange("guestId", "email", e.target.value)}
                        required
                        error={data.guestId.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.guestId.email) ? "Invalid email format" : null}
                      />
                    )}
                    {data.guestId.channels.includes("Messaging Apps") && (
                      <>
                        <Input
                          label="Phone number" placeholder="+30 697..."
                          value={data.guestId.phone}
                          isLight={isLight}
                          showValidation={showValidation}
                          onChange={(e) => handleNestedChange("guestId", "phone", e.target.value.replace(/[^0-9+]/g, ""))}
                          required
                        />
                        <div className="flex flex-col gap-4">
                          <Select
                            label="Messaging app" options={["WhatsApp", "Viber", "Other"]}
                            value={data.guestId.messagingApp}
                            isLight={isLight}
                            showValidation={showValidation}
                            onChange={(e) => handleNestedChange("guestId", "messagingApp", e.target.value)}
                            required
                          />
                          {data.guestId.messagingApp === "Other" && (
                            <Input
                              label="Specify messaging app"
                              placeholder="Telegram"
                              value={data.guestId.otherMessagingApp}
                              isLight={isLight}
                              showValidation={showValidation}
                              onChange={(e) => handleNestedChange("guestId", "otherMessagingApp", e.target.value)}
                              required
                            />
                          )}
                        </div>
                      </>
                    )}
                    {data.guestId.channels.some(c => c !== "Present in Person") && (
                      <Select
                        label="When should guests send their documents?"
                        options={["Upon booking", "48h before arrival", "24h before arrival", "Upon arrival"]}
                        value={data.guestId.timing}
                        isLight={isLight}
                        showValidation={showValidation}
                        onChange={(e) => handleNestedChange("guestId", "timing", e.target.value)}
                        required
                      />
                    )}
                    <div className="flex flex-col gap-4">
                      <Select
                        label="Required documents"
                        options={["Passport / ID", "Passport Only", "ID Only", "Other"]}
                        value={["Passport / ID", "Passport Only", "ID Only"].includes(data.guestId.requiredDocuments) ? data.guestId.requiredDocuments : (data.guestId.requiredDocuments ? "Other" : "")}
                        isLight={isLight}
                        showValidation={showValidation}
                        onChange={(e) => handleNestedChange("guestId", "requiredDocuments", e.target.value)}
                        required
                      />
                      {data.guestId.requiredDocuments !== "" && !["Passport / ID", "Passport Only", "ID Only"].includes(data.guestId.requiredDocuments) && (
                        <Input
                          label="Specify required documents"
                          placeholder="Driver's License"
                          value={data.guestId.requiredDocuments === "Other" ? "" : data.guestId.requiredDocuments}
                          isLight={isLight}
                          showValidation={showValidation}
                          onChange={(e) => handleNestedChange("guestId", "requiredDocuments", e.target.value)}
                          required
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-6">
                      <Toggle
                        label="Are Greek citizens required to provide their AFM?"
                        value={data.guestId.acceptAFM}
                        isLight={isLight}
                        onChange={(val) => handleNestedChange("guestId", "acceptAFM", val)}
                      />
                      <Toggle
                        label="Are international guests required to provide their TIN (Tax Identification Number)?"
                        value={data.guestId.acceptTIN}
                        isLight={isLight}
                        onChange={(val) => handleNestedChange("guestId", "acceptTIN", val)}
                      />
                    </div>
                  </div>
                  <Textarea
                    label="Anything else related to guest identification?"
                    value={data.additionalNotes}
                    isLight={isLight}
                    onChange={(e) => handleChange("additionalNotes", e.target.value)}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </div>
  );
};
